import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Manutencao } from '../../types';
import { manutencoesService, ManutencaoResponse } from '../../services/manutencoes';
import { parseApiDate } from '../../utils/formatters';

const ManutencaoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('geral');

  const { data, isLoading, error } = useQuery<ManutencaoResponse>({
    queryKey: ['manutencao', id],
    queryFn: () => manutencoesService.getManutencao(Number(id!)),
    enabled: Boolean(id),
  });

  // Tratar erros
  React.useEffect(() => {
    if (error) {
      toast.error('Erro ao carregar dados da manutenção');
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="w-full">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Manutenção não encontrada</h1>
          <p className="text-gray-600 mt-2">A manutenção solicitada não foi encontrada.</p>
          <Link
            to="/manutencoes"
            className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Voltar para Manutenções
          </Link>
        </div>
      </div>
    );
  }

  const manutencao: Manutencao = data.data;
  const estatisticas = data.estatisticas;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    const d = parseApiDate(date);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getTipoManutencaoColor = (tipo: string) => {
    switch (tipo) {
      case 'preventiva': return 'bg-green-100 text-green-800';
      case 'corretiva': return 'bg-red-100 text-red-800';
      case 'emergencial': return 'bg-orange-100 text-orange-800';
      case 'revisao': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'agendada': return 'bg-yellow-100 text-yellow-800';
      case 'em_andamento': return 'bg-blue-100 text-blue-800';
      case 'concluida': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const tabs = [
    { id: 'geral', name: 'Informações Gerais' },
    { id: 'estatisticas', name: 'Estatísticas' },
  ];

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Detalhes da manutenção</h1>
            <p className="text-gray-600 mt-2">
              {manutencao.descricao} - {formatDate(manutencao.data_manutencao)}
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to={`/manutencoes/${manutencao.id}/editar`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Editar
            </Link>
            <Link
              to="/manutencoes"
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Voltar
            </Link>
          </div>
        </div>

        {/* Status e Tipo */}
        <div className="flex gap-4 mb-8">
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getTipoManutencaoColor(manutencao.tipo)}`}>
            {manutencao.tipo}
          </span>
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(manutencao.status)}`}>
            {manutencao.status}
          </span>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Conteúdo das Tabs */}
        {activeTab === 'geral' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Informações Principais */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Informações da Manutenção</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Data da Manutenção</label>
                    <p className="text-sm text-gray-900">{formatDate(manutencao.data_manutencao)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Quilometragem</label>
                    <p className="text-sm text-gray-900">{formatNumber(manutencao.quilometragem)} km</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Valor Total</label>
                    <p className="text-sm text-gray-900 font-semibold">{formatCurrency(manutencao.valor_total)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Oficina</label>
                    <p className="text-sm text-gray-900">{manutencao.oficina_nome || 'Não informado'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Descrição</h2>
                <p className="text-gray-700">{manutencao.descricao}</p>
              </div>

              {manutencao.observacoes && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold mb-4">Observações</h2>
                  <p className="text-gray-700">{manutencao.observacoes}</p>
                </div>
              )}

              {/* Próximas Manutenções */}
              {(manutencao.proxima_km || manutencao.proxima_manutencao) && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold mb-4">Próxima manutenção</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {manutencao.proxima_km && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Próxima Manutenção (KM)</label>
                        <p className="text-sm text-gray-900">{formatNumber(manutencao.proxima_km)} km</p>
                      </div>
                    )}
                    {manutencao.proxima_manutencao && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Próxima Manutenção (Data)</label>
                        <p className="text-sm text-gray-900">{formatDate(manutencao.proxima_manutencao)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Garantia */}
              {(manutencao.garantia_dias || manutencao.garantia_vencimento) && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold mb-4">Garantia</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {manutencao.garantia_dias && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Garantia (Dias)</label>
                        <p className="text-sm text-gray-900">{manutencao.garantia_dias} dias</p>
                      </div>
                    )}
                    {manutencao.garantia_vencimento && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Garantia (Data)</label>
                        <p className="text-sm text-gray-900">{formatDate(manutencao.garantia_vencimento)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Veículo */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Veículo</h2>
                {manutencao.veiculo ? (
                  <div className="space-y-2">
                    <p className="font-medium">{manutencao.veiculo.placa}</p>
                    <p className="text-sm text-gray-600">
                      {manutencao.veiculo.marca} {manutencao.veiculo.modelo}
                    </p>
                    <p className="text-sm text-gray-600">
                      {manutencao.veiculo.ano_fabricacao}/{manutencao.veiculo.ano_modelo}
                    </p>
                    <Link
                      to={`/veiculos/${manutencao.veiculo.id}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Ver detalhes do veículo
                    </Link>
                  </div>
                ) : (
                  <p className="text-gray-500">Veículo não informado</p>
                )}
              </div>

              {/* Motorista */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Motorista</h2>
                {manutencao.motorista ? (
                  <div className="space-y-2">
                    <p className="font-medium">{manutencao.motorista.nome}</p>
                    <p className="text-sm text-gray-600">{manutencao.motorista.cpf}</p>
                    <Link
                      to={`/motoristas/${manutencao.motorista.id}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Ver detalhes do motorista
                    </Link>
                  </div>
                ) : (
                  <p className="text-gray-500">Motorista não informado</p>
                )}
              </div>

              {/* Peças Utilizadas */}
              {manutencao.pecas_utilizadas && manutencao.pecas_utilizadas.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-lg font-semibold mb-4">Peças utilizadas</h2>
                  <div className="space-y-3">
                    {Array.isArray(manutencao.pecas_utilizadas) ? manutencao.pecas_utilizadas.map((peca: any, index: number) => (
                      <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0">
                        <p className="font-medium text-sm">{peca.nome}</p>
                        <p className="text-xs text-gray-600">
                          {peca.quantidade}x {formatCurrency(peca.valor_unitario)} = {formatCurrency(peca.quantidade * peca.valor_unitario)}
                        </p>
                      </div>
                    )) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'estatisticas' && estatisticas && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Total de manutenções</h3>
              <p className="text-3xl font-bold text-blue-600">{estatisticas.total_manutencoes_veiculo}</p>
              <p className="text-sm text-gray-600">deste veículo</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Gasto total</h3>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(estatisticas.gasto_total_veiculo)}</p>
              <p className="text-sm text-gray-600">em manutenções</p>
            </div>

            {estatisticas.ultima_manutencao && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">Última manutenção</h3>
                <p className="text-sm text-gray-900">{estatisticas.ultima_manutencao.descricao}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {formatDate(estatisticas.ultima_manutencao.data_manutencao)}
                </p>
              </div>
            )}

            {estatisticas.proxima_manutencao && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">Próxima manutenção</h3>
                <p className="text-sm text-gray-900">{estatisticas.proxima_manutencao.descricao}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {formatDate(estatisticas.proxima_manutencao.data_manutencao)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManutencaoDetail;
